import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  signal,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor, Validator {

  // ── Inputs ───────────────────────────────────────────────────────────────────
  @Input() label = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() errorMessage = 'Ce champ est obligatoire';

  @Output() dateChange = new EventEmitter<string>();

  @ViewChild('dateInput') dateInputRef!: ElementRef<HTMLInputElement>;

  // ── State ────────────────────────────────────────────────────────────────────
  value     = signal('');
  isFocused = signal(false);
  isTouched = signal(false);

  // ── CVA ──────────────────────────────────────────────────────────────────────
  private _onChange:  (v: string) => void = () => {};
  private _onTouched: () => void          = () => {};

  writeValue(val: string): void                    { this.value.set(val ?? ''); }
  registerOnChange(fn: (v: string) => void): void  { this._onChange = fn; }
  registerOnTouched(fn: () => void): void          { this._onTouched = fn; }
  setDisabledState(isDisabled: boolean): void      { this.disabled = isDisabled; }

  // ── Validator ─────────────────────────────────────────────────────────────────
  validate(_: AbstractControl): ValidationErrors | null {
    return this.required && !this.value() ? { required: true } : null;
  }

  // ── Events ────────────────────────────────────────────────────────────────────
  onInput(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.value.set(val);
    this._onChange(val);
    this.dateChange.emit(val);
  }

  onFocus(): void { this.isFocused.set(true); }

  onBlur(): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this._onTouched();
  }

  openPicker(): void {
    if (this.disabled) return;
    const input = this.dateInputRef.nativeElement;
    // focus avant showPicker pour activer la bordure 4px (--focused / :focus-within)
    input.focus();
    try {
      input.showPicker();
    } catch {
      input.click();
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────
  get showError(): boolean { return this.required && this.isTouched() && !this.value(); }
}
